import { ThemeProvider } from "@components/ui/themeProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { Toaster } from "@components/ui/sonner";
import NotFound from "@/pages/404/NotFound";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import ClienteDashboard from "./pages/ClienteDashboard";
import { LoginPage } from "./features/login/components/LoginPage";
import ProgramaDetalles from "@/features/programas/components/ProgramaDetalles";
import ActividadPreviewPage from "@/features/programas/components/ActividadPreviewPage";
import { ActivateAccountPage } from "@/pages/ActivateAccountPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import {
  ClientesIntermedios,
  ClientesFinales as ClientesFinalesAdmin,
  Aprendices,
  Programas,
  Actividades,
  Paquetes,
  AprendicesProgramas,
  AnalyticsPage,
} from "./pages/admin";
import ActividadesAprendiz from "@/features/aprendicesActividades/componentes/ActividadesAprendiz";
import {
  VistaGeneral,
  ClientesFinales as ClientesFinalesIntermediario,
  GestionProgramas,
  PanelClientesFinales,
} from "./pages/intermediario";
import {
  DashboardLayout,
  getDashboardBasePath,
  isDashboardRole,
} from "@/features/dashboard";
import { RoleGuard } from "@/components/guards/RoleGuard";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user } = useCurrentUser();
  return user ? <>{children}</> : <Navigate to="/" replace />;
}

function RoleRedirector() {
  const { user } = useCurrentUser();
  if (!user) return <Navigate to="/login" replace />;
  const roleName = user.rol?.nombre;
  if (isDashboardRole(roleName)) {
    return <Navigate to={getDashboardBasePath(roleName)} replace />;
  }
  if (roleName === "CLIENTE") {
    return <Navigate to="/cliente" replace />;
  }
  return <Navigate to="/login" replace />;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light">
      <QueryClientProvider client={queryClient}>
        <Toaster position="top-center" richColors />
        <BrowserRouter>
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<RoleRedirector />} />
            {/* Intermediario */}
            <Route
              path="/intermediario"
              element={
                <RequireAuth>
                  <RoleGuard allowedRoles={['INTERMEDIO', 'SUPER_ADMIN']}>
                    <DashboardLayout role="INTERMEDIO" />
                  </RoleGuard>
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="vista-general" replace />} />
              <Route path="vista-general" element={<VistaGeneral />} />
              <Route
                path="clientes-finales"
                element={<ClientesFinalesIntermediario />}
              />
              <Route path="gestion-programas" element={<GestionProgramas />}>
                <Route path="detalles/:id" element={<ErrorBoundary><ProgramaDetalles /></ErrorBoundary>} />
              </Route>
              <Route
                path="panel-clientes-finales"
                element={<PanelClientesFinales />}
              />
            </Route>
            {/* Super Admin (ahora admin) */}
            <Route
              path="/admin"
              element={
                <RequireAuth>
                  <RoleGuard allowedRoles={['SUPER_ADMIN']}>
                    <DashboardLayout role="SUPER_ADMIN" />
                  </RoleGuard>
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="vista-general" replace />} />
              <Route path="vista-general" element={<ErrorBoundary><AnalyticsPage /></ErrorBoundary>} />
              <Route
                path="clientes-intermedios"
                element={<ClientesIntermedios />}
              />
              <Route
                path="clientes-finales"
                element={<ClientesFinalesAdmin />}
              />
              <Route path="aprendices" element={<Aprendices />} />
              <Route path="programas" element={<Programas />}>
                <Route path="detalles/:id" element={<ErrorBoundary><ProgramaDetalles /></ErrorBoundary>} />
                <Route
                  path="detalles/:id/preview-actividad/:actividadId"
                  element={<ActividadPreviewPage />}
                />
              </Route>
              <Route path="actividades-programas" element={<Actividades />} />
              <Route path="paquetes" element={<Paquetes />} />
              <Route
                path="aprendices-programas"
                element={<AprendicesProgramas />}
              />
              <Route
                path="aprendices-programas/:idAprendizPrograma/programa/:idPrograma/actividades"
                element={<ActividadesAprendiz />}
              />
            </Route>
            {/* Cliente */}
            <Route
              path="/cliente/*"
              element={
                <RequireAuth>
                  <RoleGuard allowedRoles={['CLIENTE', 'INTERMEDIO', 'SUPER_ADMIN']}>
                    <ClienteDashboard />
                  </RoleGuard>
                </RequireAuth>
              }
            />
            {/* Login */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/activate/:token" element={<ActivateAccountPage />} />
            <Route
              path="/reset-password/:token"
              element={<ResetPasswordPage />}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </ErrorBoundary>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
